import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { StatusModel } from "../../../../factories/statuses/models/status.model";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useLazyGetStatusesQuery,
  useUpdateSubStatusMutation,
} from "../../../../slices/StatusesSlice";
import { useLazyGetWorkTypesQuery } from "../../../../slices/WorkTypeSlice";
import {
  StatusAssignmentDrafts,
  StatusesPageController,
} from "../model/statuses.types";
import { getSubStatusAssignedWorkTypeIds } from "../model/statuses.utils";

const getAssignmentDrafts = (statuses: StatusModel[]) => {
  return statuses.reduce<StatusAssignmentDrafts>((acc, status) => {
    const subStatusDrafts =
      status.workTypeSubStatuses?.reduce<StatusAssignmentDrafts>(
        (subStatusAcc, subStatus) => {
          const assignedWorkTypeIds =
            getSubStatusAssignedWorkTypeIds(subStatus);

          if (!assignedWorkTypeIds.length) {
            return subStatusAcc;
          }

          return {
            ...subStatusAcc,
            [subStatus.id]: assignedWorkTypeIds,
          };
        },
        {},
      ) ?? {};

    return {
      ...acc,
      ...subStatusDrafts,
    };
  }, {});
};

const findSubStatusById = (statuses: StatusModel[], subStatusId: string) => {
  for (const status of statuses) {
    const subStatus = status.workTypeSubStatuses?.find(
      (item) => item.id === subStatusId,
    );

    if (subStatus) {
      return subStatus;
    }
  }

  return null;
};

export const useStatusesPageController = (): StatusesPageController => {
  const orgId = getLinkedFirm()?.orgId || "";

  const [assignmentDrafts, setAssignmentDrafts] =
    useState<StatusAssignmentDrafts>({});
  const [isSavingAssignment, setIsSavingAssignment] = useState(false);

  const [getStatuses, { isLoading: isLoadingStatuses, data: statuses = [] }] =
    useLazyGetStatusesQuery();
  const [
    getWorkTypes,
    { isLoading: isLoadingWorkTypes, data: workTypes = [] },
  ] = useLazyGetWorkTypesQuery();
  const [updateSubStatus] = useUpdateSubStatusMutation();

  const refreshStatuses = useCallback(async () => {
    if (!orgId) {
      return;
    }

    await getStatuses({ orgId }).unwrap();
  }, [getStatuses, orgId]);

  const refreshWorkTypes = useCallback(async () => {
    if (!orgId) {
      return;
    }

    await getWorkTypes({ orgId, status: "active" }).unwrap();
  }, [getWorkTypes, orgId]);

  const saveAssignment = useCallback(
    async (subStatusId: string, workTypeIds: string[]) => {
      const subStatus = findSubStatusById(statuses, subStatusId);

      if (!subStatus) {
        toast("Substatus not found", {
          type: "error",
        });
        return;
      }

      setIsSavingAssignment(true);
      const previousWorkTypeIds =
        assignmentDrafts[subStatusId] ??
        getSubStatusAssignedWorkTypeIds(subStatus);

      try {
        setAssignmentDrafts((prev) => ({
          ...prev,
          [subStatusId]: workTypeIds,
        }));

        await updateSubStatus({
          ...subStatus,
          serviceIds: workTypeIds,
        }).unwrap();
        await Promise.all([refreshStatuses(), refreshWorkTypes()]);

        toast("Work type assignments updated", {
          type: "success",
        });
      } catch {
        setAssignmentDrafts((prev) => ({
          ...prev,
          [subStatusId]: previousWorkTypeIds,
        }));
        toast("Something went wrong", {
          type: "error",
        });
      } finally {
        setIsSavingAssignment(false);
      }
    },
    [
      assignmentDrafts,
      refreshStatuses,
      refreshWorkTypes,
      statuses,
      updateSubStatus,
    ],
  );

  useEffect(() => {
    void refreshStatuses();
    void refreshWorkTypes();
  }, [refreshStatuses, refreshWorkTypes]);

  useEffect(() => {
    setAssignmentDrafts(getAssignmentDrafts(statuses));
  }, [statuses]);

  return {
    isLoading: isLoadingStatuses || isLoadingWorkTypes,
    statuses,
    workTypes,
    assignmentDrafts,
    isSavingAssignment,
    refreshStatuses,
    saveAssignment,
  };
};
